import { FC, ReactElement } from 'react';
import classnames from 'classnames';

interface TooltipWrapperProps {
  tooltip: string;
  children: ReactElement;
  id?: string;
  className?: string;
  position?: 'top' | 'top-left' | 'bottom' | 'bottom-left';
  showTooltip?: boolean;
  customTooltipClassName?: string;
  /** When true, tooltip text may wrap; default is single-line (nowrap). */
  allowMultilineTooltip?: boolean;
}

export const TooltipWrapper: FC<TooltipWrapperProps> = ({
  tooltip,
  children,
  id,
  className,
  position = 'top',
  showTooltip = true,
  customTooltipClassName = '',
  allowMultilineTooltip = false
}) => {
  const bodySurfaceClass = allowMultilineTooltip
    ? 'w-[min(260px,calc(100vw-2rem))] break-words px-3 py-2 text-left text-xs leading-snug ' +
      'text-primaryText shadow-md'
    : 'px-2 py-1 text-xs text-primaryText shadow-md';
  const isTopPosition = position === 'top' || position === 'top-left';

  return (
    <div className={`group relative ${className}`} id={id}>
      {children}
      {showTooltip && (
        <div
          className={classnames(
            'absolute z-20 hidden rounded bg-white',
            allowMultilineTooltip ? '' : 'whitespace-nowrap',
            bodySurfaceClass,
            isTopPosition && 'bottom-full mb-3',
            position === 'bottom-left' && '-bottom-8 translate-x-[-93%]',
            position === 'top' && 'left-1/2 -translate-x-1/2',
            position === 'top-left' && 'left-0',
            'before:absolute before:border-4 before:border-x-transparent',
            isTopPosition && 'before:top-[100%] before:border-t-white before:border-b-transparent',
            position === 'bottom-left' && 'before:bottom-[100%] before:border-b-white before:border-t-transparent',
            position === 'top' && 'before:left-1/2 before:-translate-x-1/2',
            position === 'top-left' && 'before:left-2',
            position === 'bottom-left' && 'before:left-[93%]',
            'group-hover:block',
            customTooltipClassName
          )}
          role="tooltip"
          aria-hidden="true">
          {tooltip}
        </div>
      )}
    </div>
  );
};
